import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, SectionTitle } from "../primitives";

type ContractType = {
  id: number;
  category: string;
  name: string;
  short_code: string | null;
  is_amendment_style: boolean;
  sort_order: number;
  is_active: boolean;
};

type DraftType = { name: string; short_code: string; is_amendment_style: boolean };

const emptyDraft: DraftType = { name: "", short_code: "", is_amendment_style: false };

export const ContractTypeManager = () => {
  const [types, setTypes] = useState<Record<string, ContractType[]>>({ Customer: [], Vendor: [], Corporate: [] });
  const [drafts, setDrafts] = useState<Record<string, DraftType>>({
    Customer: { ...emptyDraft },
    Vendor: { ...emptyDraft },
    Corporate: { ...emptyDraft },
  });
  const [editing, setEditing] = useState<Record<number, DraftType>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTypes = async () => {
    setError(null);
    const data = await apiCall("/contract-types");
    if (data?.grouped) {
      setTypes({
        Customer: data.grouped.Customer || [],
        Vendor: data.grouped.Vendor || [],
        Corporate: data.grouped.Corporate || [],
      });
    }
    setLoading(false);
  };
  useEffect(() => { loadTypes(); }, []);

  const addType = async (category: string) => {
    const draft = drafts[category];
    if (!draft.name.trim()) return;
    const body: any = { category, name: draft.name.trim() };
    if (draft.short_code.trim()) body.short_code = draft.short_code.trim();
    if (draft.is_amendment_style) body.is_amendment_style = true;
    const result = await apiCall("/contract-types", { method: "POST", body: JSON.stringify(body) });
    if (result && result.error) {
      setError(result.error);
      return;
    }
    setDrafts((p) => ({ ...p, [category]: { ...emptyDraft } }));
    loadTypes();
  };

  const startEdit = (t: ContractType) => {
    setEditing((p) => ({
      ...p,
      [t.id]: { name: t.name, short_code: t.short_code || "", is_amendment_style: t.is_amendment_style || false },
    }));
  };

  const saveEdit = async (t: ContractType) => {
    const e = editing[t.id];
    if (!e) return;
    const body: any = {
      name: e.name,
      short_code: e.short_code.trim() || null,
      is_amendment_style: e.is_amendment_style,
    };
    const result = await apiCall(`/contract-types/${t.id}`, { method: "PUT", body: JSON.stringify(body) });
    if (result && result.error) {
      setError(result.error);
      return;
    }
    setEditing((p) => {
      const n = { ...p };
      delete n[t.id];
      return n;
    });
    loadTypes();
  };

  const cancelEdit = (id: number) => {
    setEditing((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });
  };

  const removeType = async (id: number) => {
    await apiCall(`/contract-types/${id}`, { method: "DELETE" });
    loadTypes();
  };

  if (loading) return <div style={{ padding: 20, color: T.txM }}>Loading...</div>;

  const inputStyle: React.CSSProperties = {
    padding: "5px 8px", fontSize: 11, borderRadius: 4,
    border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx,
    outline: "none", fontFamily: "inherit",
  };

  return (
    <div>
      <SectionTitle sub="Manage contract type categories. Short Code is used in REQ-/CON- folder paths and filenames. Codes must be unique within each category but can be reused across categories. Amendment-style? marks types whose records live in their parent's repository folder.">Contract Types</SectionTitle>
      {error && (
        <div style={{ padding: "8px 12px", margin: "0 0 12px 0", borderRadius: 4, background: "#3a1a1a", border: "1px solid #663", color: "#fcc", fontSize: 11 }}>
          {error} <button onClick={() => setError(null)} style={{ marginLeft: 8, background: "none", border: "none", color: "#fcc", cursor: "pointer" }}>x</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        {["Customer", "Vendor", "Corporate"].map(cat => (
          <Card key={cat}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>{cat} Contracts</div>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 36px 24px 24px", gap: 4, fontSize: 10, color: T.txD, padding: "0 0 4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <span>Name</span><span>Short Code</span><span title="Amendment-style">A?</span><span /><span />
            </div>
            {(types[cat] || []).map((t) => {
              const e = editing[t.id];
              if (e) {
                return (
                  <div key={t.id} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 36px 24px 24px", gap: 4, alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                    <input value={e.name} onChange={ev => setEditing(p => ({ ...p, [t.id]: { ...e, name: ev.target.value } }))} style={inputStyle} />
                    <input value={e.short_code} maxLength={16} onChange={ev => setEditing(p => ({ ...p, [t.id]: { ...e, short_code: ev.target.value } }))} placeholder="—" style={inputStyle} />
                    <input type="checkbox" checked={e.is_amendment_style} onChange={ev => setEditing(p => ({ ...p, [t.id]: { ...e, is_amendment_style: ev.target.checked } }))} title="Amendment-style? When ticked, files for records of this type live in their parent's repository folder." style={{ accentColor: T.acc }} />
                    <button onClick={() => saveEdit(t)} title="Save" style={{ background: "none", border: "none", color: T.acc, cursor: "pointer", fontSize: 13 }}>✓</button>
                    <button onClick={() => cancelEdit(t.id)} title="Cancel" style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 11 }}>x</button>
                  </div>
                );
              }
              return (
                <div key={t.id} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 36px 24px 24px", gap: 4, alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <span style={{ fontSize: 11, color: T.tx }}>{t.name}</span>
                  <span style={{ fontSize: 10, color: t.short_code ? T.tx : T.txD, fontFamily: "monospace" }}>{t.short_code || "—"}</span>
                  <span style={{ fontSize: 11, textAlign: "center", color: t.is_amendment_style ? T.acc : T.txD }}>{t.is_amendment_style ? "✓" : ""}</span>
                  <button onClick={() => startEdit(t)} title="Edit" style={{ background: "none", border: "none", color: T.txM, cursor: "pointer", fontSize: 11 }}>✎</button>
                  <button onClick={() => removeType(t.id)} title="Remove" style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 11 }}>x</button>
                </div>
              );
            })}
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
              <input
                value={drafts[cat].name}
                onChange={(e) => setDrafts((p) => ({ ...p, [cat]: { ...p[cat], name: e.target.value } }))}
                onKeyDown={(e) => { if (e.key === "Enter") addType(cat); }}
                placeholder="New type name…"
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input
                  value={drafts[cat].short_code}
                  maxLength={16}
                  onChange={(e) => setDrafts((p) => ({ ...p, [cat]: { ...p[cat], short_code: e.target.value } }))}
                  placeholder="Short code"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <label style={{ fontSize: 10, color: T.txM, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={drafts[cat].is_amendment_style}
                    onChange={(e) => setDrafts((p) => ({ ...p, [cat]: { ...p[cat], is_amendment_style: e.target.checked } }))}
                    style={{ accentColor: T.acc }}
                  />
                  Amend-style
                </label>
                <Btn v="secondary" sz="sm" onClick={() => addType(cat)}>Add</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
